// src/UserPreferencesPage.js
import React, { useState, useEffect } from 'react';
import { getAuth, updateProfile } from 'firebase/auth';
import { db, functions } from './firebase.js';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { flattenAuthNavItems, MAX_USER_QUICK_LINKS } from './navMenu.js';

const NAV_ITEMS = flattenAuthNavItems();

const UserPreferencesPage = ({ user, isAdmin, isMobile }) => {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [emergencyNotifications, setEmergencyNotifications] = useState(false);
  const [updateNotifications, setUpdateNotifications] = useState(false);
  const [quickLinks, setQuickLinks] = useState([]);
  const [defaultGateLotId, setDefaultGateLotId] = useState('');
  const [gateLots, setGateLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        navigate('/');
        return;
      }
      setLoading(true);
      try {
        await user.getIdToken(true);
        setEmail(user.email || '');
        setDisplayName(user.displayName || '');

        const [prefDoc, platesRes] = await Promise.all([
          getDoc(doc(db, 'user_preferences', user.uid)),
          httpsCallable(functions, 'getGatePlates')().catch(() => ({ data: { lots: [] } })),
        ]);

        const lots = platesRes.data?.lots || [];
        setGateLots(lots);

        if (prefDoc.exists()) {
          const data = prefDoc.data();
          setEmergencyNotifications(data.emergencyNotifications || false);
          setUpdateNotifications(data.updateNotifications || false);
          setQuickLinks(Array.isArray(data.quickLinks) ? data.quickLinks : []);
          setDefaultGateLotId(data.defaultGateLotId || lots[0]?.lotId || '');
        } else if (lots.length) {
          setDefaultGateLotId(lots[0].lotId);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [user, navigate]);

  const toggleQuickLink = (path) => {
    setQuickLinks((prev) => {
      if (prev.includes(path)) return prev.filter((p) => p !== path);
      if (prev.length >= MAX_USER_QUICK_LINKS) return prev;
      return [...prev, path];
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      const auth = getAuth();
      if (displayName !== user.displayName) {
        await updateProfile(auth.currentUser, { displayName });
      }

      await setDoc(doc(db, 'user_preferences', user.uid), {
        emergencyNotifications,
        updateNotifications,
        quickLinks,
        defaultGateLotId: defaultGateLotId || null,
        updatedAt: new Date(),
        updatedBy: user.uid,
      }, { merge: true });
      setSuccess('Preferences saved successfully!');
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError('Failed to save preferences: ' + err.message);
    }
  };

  const handleClose = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return <div className="preferences-page">Loading...</div>;
  }

  return (
    <div className={`preferences-page ${isMobile ? 'preferences-page-mobile' : ''}`}>
      <h1 className="preferences-page-title">User Preferences</h1>
      {success && <p className="preferences-success">{success}</p>}
      {error && <p className="preferences-error">{error}</p>}
      <form onSubmit={handleSave} className="preferences-form">
        <div className="preferences-section">
          <h2>Account</h2>
          <label className="preferences-field">
            <span>Email (read-only)</span>
            <input type="email" value={email} disabled />
          </label>
          <label className="preferences-field">
            <span>Display Name</span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your name"
            />
          </label>
        </div>

        <div className="preferences-section">
          <h2>Quick Links</h2>
          <p className="preferences-hint">
            Pick up to {MAX_USER_QUICK_LINKS} shortcuts for your dashboard home page.
          </p>
          <div className="preferences-quick-links-grid">
            {NAV_ITEMS.map((item) => {
              const checked = quickLinks.includes(item.path);
              const disabled = !checked && quickLinks.length >= MAX_USER_QUICK_LINKS;
              return (
                <label key={item.path} className={`preferences-quick-link-option${disabled ? ' is-disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleQuickLink(item.path)}
                  />
                  <span>{item.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        {gateLots.length > 0 ? (
          <div className="preferences-section">
            <h2>Gate Access</h2>
            <p className="preferences-hint">Default lot used by the Open Gate button in the header.</p>
            <label className="preferences-field">
              <span>Default lot</span>
              <select
                value={defaultGateLotId}
                onChange={(e) => setDefaultGateLotId(e.target.value)}
              >
                {gateLots.map((lot) => (
                  <option key={lot.lotId} value={lot.lotId}>
                    {lot.label || lot.lotId}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}

        <div className="preferences-section">
          <h2>Notifications</h2>
          <label className="preferences-checkbox">
            <input
              type="checkbox"
              checked={emergencyNotifications}
              onChange={(e) => setEmergencyNotifications(e.target.checked)}
            />
            Receive email emergency notifications
          </label>
          <label className="preferences-checkbox">
            <input
              type="checkbox"
              checked={updateNotifications}
              onChange={(e) => setUpdateNotifications(e.target.checked)}
            />
            Receive updates for message board posts, HOA documents, and calendar events
          </label>
        </div>

        <div className="preferences-actions">
          <button type="submit" className="preferences-btn preferences-btn-primary">
            Save Preferences
          </button>
          <button type="button" onClick={handleClose} className="preferences-btn preferences-btn-secondary">
            Close
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserPreferencesPage;